import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { JournalFormattedView } from './JournalFormattedView';
import { JournalRichTextEditor } from './JournalRichTextEditor';
import { JournalAutosaveStatus } from './JournalAutosaveStatus';
import { useJournalWritingDraft } from '@/hooks/useJournalWritingDraft';
import { useAuth } from '@/lib/auth';
import { useJournalFinishConfirmation } from '@/hooks/useJournalFinishConfirmation';
import { formatCourseDateTime } from '@/lib/courseDateFormat';
import { JournalAttachments } from './JournalAttachments';
import { JournalDraftFinalizeActions } from './JournalDraftFinalizeActions';
import {
  useCreateJournalAmendment,
  useDiscardJournalAmendment,
  useFinalizeJournalAmendment,
  useJournalAmendments,
  useUpdateJournalAmendment,
  type JournalAmendmentRow,
} from '@/hooks/useJournal';
import { useUploadJournalAmendmentAttachment } from '@/hooks/useJournalAttachments';
import {
  canFinalizeJournalContent,
  persistJournalWithIntent,
  JournalFinalizeAfterSaveError,
  type JournalPersistIntent,
} from '@/utils/journalPersist';
import {
  isCorrectionMutable,
  planCorrectionCancel,
  resolveCorrectionDiscardPrompt,
} from '@/utils/journalCorrection';

interface JournalCorrectionPanelProps {
  clinicId: string;
  patientId: string;
  entryId: string;
  canAmend: boolean;
  startToken?: number;
  variant?: 'inline' | 'write';
}

export function JournalCorrectionPanel({
  clinicId,
  patientId,
  entryId,
  canAmend,
  startToken = 0,
  variant = 'inline',
}: JournalCorrectionPanelProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const finishConfirm = useJournalFinishConfirmation({
    clinicId,
    patientId,
  });
  const rootRef = useRef<HTMLDivElement>(null);
  const amendmentsQuery = useJournalAmendments(entryId);
  const createAmendment = useCreateJournalAmendment(clinicId);
  const updateAmendment = useUpdateJournalAmendment(clinicId);
  const finalizeAmendment = useFinalizeJournalAmendment(clinicId);
  const discardAmendment = useDiscardJournalAmendment(clinicId);
  const uploadAmendmentAttachment = useUploadJournalAmendmentAttachment(clinicId);
  const [draftContent, setDraftContent] = useState('');
  const [draftContentHtml, setDraftContentHtml] = useState<string | null>(null);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [composing, setComposing] = useState(false);
  const [persistIntent, setPersistIntent] = useState<JournalPersistIntent | null>(null);
  const [discardPromptOpen, setDiscardPromptOpen] = useState(false);
  const [activeDraft, setActiveDraft] = useState<JournalAmendmentRow | null>(null);
  const amendments = amendmentsQuery.data ?? [];
  const listedIds = new Set(amendments.map((row) => row.id));
  const visibleAmendments =
    activeDraft && !listedIds.has(activeDraft.id) && isCorrectionMutable(activeDraft)
      ? [...amendments, activeDraft]
      : amendments;
  const openDraft = visibleAmendments.find((row) => isCorrectionMutable(row));
  const busy = persistIntent != null || finishConfirm.pending || discardAmendment.isPending;
  const {
    status: draftStatus,
    lastSavedAt: draftLastSavedAt,
    restored: restoredDraft,
    dismissRestored,
    deleteDraft,
  } = useJournalWritingDraft({
    clinicId,
    patientId,
    userId: user?.id ?? null,
    scope: editingId
      ? { kind: 'amendment', amendmentId: editingId }
      : { kind: 'amendment-new', entryId },
    title: '',
    contentMd: draftContent,
    contentHtml: draftContentHtml,
    enabled: composing || Boolean(editingId),
    restoreOnMount: composing || Boolean(editingId),
    baselineTitle: '',
  });

  const closeUnsavedEditor = () => {
    setComposing(false);
    setEditingId(null);
    setActiveDraft(null);
    setDraftContent('');
    setDraftContentHtml(null);
    setPendingFiles([]);
    setDiscardPromptOpen(false);
  };

  useEffect(() => {
    if (!restoredDraft) return;
    setDraftContent(restoredDraft.content_md);
    setDraftContentHtml(restoredDraft.content_html);
    if (!editingId) {
      setComposing(true);
    }
    dismissRestored();
  }, [dismissRestored, editingId, restoredDraft]);

  const startCorrection = () => {
    setComposing(true);
    setEditingId(null);
    setDraftContent('');
    setDraftContentHtml(null);
    setPendingFiles([]);
  };

  useEffect(() => {
    if (!startToken || !canAmend) return;
    const draft = visibleAmendments.find((row) => isCorrectionMutable(row));
    if (draft) {
      setComposing(false);
      setEditingId(draft.id);
      setActiveDraft(draft);
      setDraftContent(draft.content_md);
      setDraftContentHtml(draft.content_html);
      setPendingFiles([]);
      return;
    }
    startCorrection();
  }, [startToken, canAmend]);

  const persistPendingFiles = async (amendmentId: string, files: File[]) => {
    for (const file of files) {
      await uploadAmendmentAttachment.mutateAsync({
        clinicId,
        patientId,
        amendmentId,
        file,
      });
    }
  };

  const saveRow = async (existing: JournalAmendmentRow | null, content: string) => {
    const saved = existing
      ? await updateAmendment.mutateAsync({
          id: existing.id,
          content_md: content,
          content_html: draftContentHtml,
          row_version: existing.row_version,
        })
      : await createAmendment.mutateAsync({
          journal_entry_id: entryId,
          content_md: content,
          content_html: draftContentHtml,
        });
    if (pendingFiles.length > 0) {
      await persistPendingFiles(saved.id, pendingFiles);
      setPendingFiles([]);
    }
    setActiveDraft(saved);
    return saved;
  };

  const handlePersist = async (
    intent: JournalPersistIntent,
    existing: JournalAmendmentRow | null,
    content: string = draftContent,
  ) => {
    if (intent === 'finalize' && !(await finishConfirm.confirmFinish())) return;
    setPersistIntent(intent);
    try {
      const { entry: saved, finalized } = await persistJournalWithIntent(
        intent,
        () => saveRow(existing, content),
        (row) =>
          finalizeAmendment.mutateAsync({
            id: row.id,
            row_version: row.row_version,
          }),
      );
      if (finalized) {
        await deleteDraft();
        closeUnsavedEditor();
        toast.success(t('journal.correction_finalized'));
      } else {
        await deleteDraft();
        setComposing(false);
        setEditingId(saved.id);
        setActiveDraft(saved);
        toast.success(t('journal.draft_saved'));
      }
    } catch (error) {
      toast.error(intent === 'finalize' ? t('journal.finalize_failed') : t('common.error'));
      if (error instanceof JournalFinalizeAfterSaveError) {
        await deleteDraft();
        setComposing(false);
        setEditingId(error.entryId);
      }
    } finally {
      setPersistIntent(null);
    }
  };

  const handleCancel = (hasPersistedDraft: boolean) => {
    const plan = planCorrectionCancel(hasPersistedDraft);
    switch (plan) {
      case 'close_without_record':
        closeUnsavedEditor();
        return;
      case 'confirm_discard':
        setDiscardPromptOpen(true);
        return;
      default: {
        const _exhaustive: never = plan;
        return _exhaustive;
      }
    }
  };

  const handleDiscardPrompt = async (choice: 'discard' | 'keep_editing') => {
    const resolution = resolveCorrectionDiscardPrompt(choice);
    switch (resolution) {
      case 'remain_editing':
        setDiscardPromptOpen(false);
        return;
      case 'discard_draft': {
        const draftId = editingId ?? openDraft?.id;
        if (!draftId) {
          closeUnsavedEditor();
          return;
        }
        try {
          await discardAmendment.mutateAsync(draftId);
          await deleteDraft();
          closeUnsavedEditor();
          toast.success(t('journal.correction_draft_discarded'));
        } catch {
          toast.error(t('common.error'));
        }
        return;
      }
      default: {
        const _exhaustive: never = resolution;
        return _exhaustive;
      }
    }
  };

  const renderEditor = (row: JournalAmendmentRow | null) => (
    <>
      <div className="mt-2">
        <JournalRichTextEditor
          clinicId={clinicId}
          patientId={patientId}
          html={draftContentHtml}
          text={draftContent}
          onChange={(value) => {
            setDraftContent(value.text);
            setDraftContentHtml(value.html);
          }}
          minHeightClass="min-h-[8rem]"
          placeholder={t('journal.correction_placeholder')}
        />
        <JournalAutosaveStatus
          status={draftStatus}
          lastSavedAt={draftLastSavedAt}
        />
      </div>
      <div className="mt-3">
        <JournalAttachments
          clinicId={clinicId}
          patientId={patientId}
          amendmentId={row?.id ?? null}
          pendingFiles={pendingFiles}
          onPendingFilesChange={setPendingFiles}
          canEdit
        />
      </div>
      {variant === 'write' ? null : (
        <div className="mt-2 flex justify-end gap-2">
          <Button
            size="sm"
            variant="secondary"
            onClick={() => handleCancel(Boolean(row))}
            disabled={busy}
          >
            {t('common.cancel')}
          </Button>
          <JournalDraftFinalizeActions
            busy={busy}
            pendingIntent={persistIntent ?? (finishConfirm.pending ? 'finalize' : null)}
            canFinalize={canFinalizeJournalContent(draftContent)}
            onSaveDraft={() => void handlePersist('draft', row)}
            onFinalize={() => void handlePersist('finalize', row)}
            size="sm"
          />
        </div>
      )}
    </>
  );

  const writeFooter = (row: JournalAmendmentRow | null) =>
    variant === 'write' && (composing || editingId) ? (
      <div className="jw-foot">
        <p className="hint">{t('journal.correction_locked_hint')}</p>
        <div className="jw-foot-btns">
          <Button
            variant="tertiary"
            onClick={() => handleCancel(Boolean(row))}
            disabled={busy}
          >
            {t('journal.discard_correction')}
          </Button>
          <JournalDraftFinalizeActions
            busy={busy}
            pendingIntent={persistIntent ?? (finishConfirm.pending ? 'finalize' : null)}
            canFinalize={canFinalizeJournalContent(draftContent)}
            onSaveDraft={() => void handlePersist('draft', row)}
            onFinalize={() => void handlePersist('finalize', row)}
            finalizeLabel={t('journal.finalize_correction')}
          />
        </div>
      </div>
    ) : null;

  const editingRow =
    (editingId ? visibleAmendments.find((row) => row.id === editingId) : null) ??
    (composing ? null : null);

  return (
    <>
    <div
      ref={rootRef}
      tabIndex={-1}
      className={
        variant === 'write'
          ? 'jw-corr outline-none'
          : 'mt-4 space-y-3 border-t border-ds-line pt-4 outline-none'
      }
    >
      {discardPromptOpen && (
        <Modal
          title={t('journal.discard_correction_confirm')}
          onClose={() => void handleDiscardPrompt('keep_editing')}
        >
          <div className="flex justify-end gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => void handleDiscardPrompt('keep_editing')}
              disabled={discardAmendment.isPending}
            >
              {t('journal.keep_editing')}
            </Button>
            <Button
              variant="danger"
              size="sm"
              onClick={() => void handleDiscardPrompt('discard')}
              disabled={discardAmendment.isPending}
            >
              {discardAmendment.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                t('journal.discard_correction')
              )}
            </Button>
          </div>
        </Modal>
      )}

      {visibleAmendments.map((row) => {
        const isEditing = editingId === row.id && isCorrectionMutable(row);
        return (
          <div
            key={row.id}
            className={
              variant === 'write'
                ? 'jw-corr-box'
                : 'rounded-md border border-ds-line bg-ds-sunk p-3 bg-ds-card/40'
            }
          >
            <p className="text-xs font-semibold uppercase tracking-wide text-ds-teal">
              {t('journal.correction_heading')}
            </p>
            <p className="mt-1 text-xs text-ds-muted">
              {formatCourseDateTime(row.finalized_at ?? row.created_at, 'da')}
              {row.author_name_snapshot ? ` · ${row.author_name_snapshot}` : ''}
              {row.author_title_snapshot ? ` · ${row.author_title_snapshot}` : ''}
              {row.finalized_at
                ? ` · ${t('journal.filter_status_finalized')}`
                : ` · ${t('journal.filter_status_draft')}`}
            </p>
            {isEditing ? (
              renderEditor(row)
            ) : (
              <>
                <div className="mt-2">
                  <JournalFormattedView html={row.content_html} text={row.content_md} />
                </div>
                <div className="mt-3">
                  <JournalAttachments
                    clinicId={clinicId}
                    patientId={patientId}
                    amendmentId={row.id}
                    canEdit={false}
                  />
                </div>
                {canAmend && isCorrectionMutable(row) && (
                  <div className="mt-2 flex justify-end gap-2">
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => {
                        setComposing(false);
                        setEditingId(row.id);
                        setActiveDraft(row);
                        setDraftContent(row.content_md);
                        setDraftContentHtml(row.content_html);
                        setPendingFiles([]);
                      }}
                    >
                      {t('journal.edit')}
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => {
                        setDraftContent(row.content_md);
                        setDraftContentHtml(row.content_html);
                        void handlePersist('finalize', row, row.content_md);
                      }}
                      disabled={busy || !canFinalizeJournalContent(row.content_md)}
                    >
                      {persistIntent === 'finalize' ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        t('journal.finalize')
                      )}
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        );
      })}

      {composing && !openDraft && (
        <div
          className={
            variant === 'write'
              ? 'jw-corr-box'
              : 'rounded-md border border-ds-line bg-ds-sunk p-3 bg-ds-card/40'
          }
        >
          <p className="k">{t('journal.correction_heading')}</p>
          {variant === 'write' ? (
            <p className="mm">{t('journal.correction_keep_original')}</p>
          ) : null}
          {renderEditor(null)}
        </div>
      )}

      {canAmend && !openDraft && !composing && (
        <div className="flex justify-end">
          <Button size="sm" variant="secondary" onClick={startCorrection}>
            {t('journal.add_correction')}
          </Button>
        </div>
      )}
    </div>
    {writeFooter(editingRow)}
    {finishConfirm.dialog}
    </>
  );
}
